<template>
  <main class="setup-shell">
    <section class="setup-panel" aria-labelledby="setup-title">
      <header class="setup-header">
        <div>
          <p class="setup-eyebrow">YESMED · PAINEL DE CHAMADAS</p>
          <h1 id="setup-title">Escolha onde esta TV sera usada</h1>
          <p>Selecione um painel cadastrado para iniciar as chamadas.</p>
        </div>
        <span v-if="!loading && !catalogError" class="service-status">
          <span aria-hidden="true">●</span> SERVICO CONECTADO
        </span>
      </header>

      <div v-if="loading" class="setup-state" role="status">
        <span class="loading-dot" aria-hidden="true"></span>
        Carregando paineis disponiveis...
      </div>

      <template v-else>
        <div v-if="invalidCode" class="setup-notice setup-notice--warning" role="alert">
          O codigo "{{ invalidCode }}" nao e valido. Escolha um painel ou informe um codigo inteiro.
        </div>

        <div v-if="catalogError" class="setup-notice setup-notice--error" role="alert">
          Nao foi possivel consultar os paineis cadastrados. Use o codigo manual ou contate o suporte.
        </div>

        <div v-else-if="!paineis.length" class="setup-notice" role="status">
          Nenhum painel disponivel foi encontrado. Informe o codigo manualmente.
        </div>

        <label v-if="showSearch" class="search-field">
          <span>BUSCAR PAINEL</span>
          <input
            v-model.trim="search"
            type="search"
            placeholder="Nome, unidade ou codigo"
            autocomplete="off"
          >
        </label>

        <div v-if="paineis.length" class="catalog" aria-live="polite">
          <section v-for="group in groups" :key="group.razao_social" class="unit-group">
            <h2>{{ group.razao_social }}</h2>
            <div class="panel-grid">
              <button
                v-for="painel in group.paineis"
                :key="painel.codigo"
                type="button"
                class="panel-card"
                @click="openPanel(painel.codigo)"
              >
                <span class="panel-card-topline">
                  <span>PAINEL {{ String(painel.codigo).padStart(2, '0') }}</span>
                  <span class="available-label">DISPONIVEL</span>
                </span>
                <strong>{{ painel.descricao }}</strong>
                <span class="panel-action">USAR ESTE PAINEL</span>
              </button>
            </div>
          </section>

          <p v-if="!groups.length" class="no-search-results">
            Nenhum painel corresponde a "{{ search }}".
          </p>
        </div>
      </template>

      <section class="manual-section">
        <button
          type="button"
          class="manual-toggle"
          :aria-expanded="String(manualOpen)"
          @click="manualOpen = !manualOpen"
        >
          {{ manualOpen ? 'Ocultar codigo manual' : 'Inserir codigo manualmente' }}
        </button>

        <form v-if="manualOpen" class="manual-form" @submit.prevent="submitManual">
          <label for="manual-code">CODIGO DO PAINEL</label>
          <div class="manual-controls">
            <input
              id="manual-code"
              v-model.trim="manualCode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              placeholder="Ex.: 1"
              autocomplete="off"
              @input="manualError = ''"
            >
            <button type="submit">ABRIR PAINEL</button>
          </div>
          <p v-if="manualError" class="manual-error" role="alert">{{ manualError }}</p>
        </form>
      </section>

      <footer class="support-footer">
        <div>
          <strong>Precisa de suporte?</strong>
          Informe a unidade, o codigo do painel, o navegador e o horario do problema.
        </div>
        <div class="current-url" :title="currentUrl">URL atual: {{ currentUrl }}</div>
      </footer>
    </section>
  </main>
</template>

<script>
import { getPaineis } from '../services'
import {
  catalogDecision,
  groupCatalog,
  normalizeCatalog
} from '../utils/painel-catalog.mjs'
import { isPainelCodigoValido } from '../utils/painel-state.mjs'

export default {
  name: 'PainelSetup',
  data() {
    return {
      loading: true,
      catalogError: false,
      paineis: [],
      search: '',
      manualOpen: false,
      manualCode: '',
      manualError: ''
    }
  },
  computed: {
    groups() {
      return groupCatalog(this.paineis, this.search)
    },
    showSearch() {
      return this.paineis.length > 6
    },
    invalidCode() {
      return this.$route.query.codigoInvalido || ''
    },
    currentUrl() {
      return window.location.href
    }
  },
  created() {
    this._destroyed = false
  },
  async mounted() {
    try {
      const response = await getPaineis(this.$axios)
      if (this._destroyed) return
      const paineis = normalizeCatalog(response.data)
      if (!paineis) throw new Error('Invalid panel catalog payload')
      if (catalogDecision(paineis) === 'single') {
        await this.openPanel(paineis[0].codigo, true)
        return
      }
      this.paineis = paineis
      this.manualOpen = paineis.length === 0 || Boolean(this.invalidCode)
    } catch (error) {
      if (this._destroyed) return
      this.catalogError = true
      this.manualOpen = true
      if (process.env.NODE_ENV !== 'production') console.warn(error)
    } finally {
      if (!this._destroyed) this.loading = false
    }
  },
  beforeDestroy() {
    this._destroyed = true
  },
  methods: {
    openPanel(codigo, replace = false) {
      const target = { name: 'painel', params: { painel: String(codigo) } }
      return replace ? this.$router.replace(target) : this.$router.push(target)
    },
    submitManual() {
      const code = String(this.manualCode).trim()
      if (!isPainelCodigoValido(code)) {
        this.manualError = 'Informe um codigo inteiro valido.'
        return
      }
      this.manualError = ''
      this.openPanel(code)
    }
  }
}
</script>

<style scoped>
.setup-shell {
  --blue: #2f6595;
  --blue-dark: #173f61;
  --ink: #17212b;
  --muted: #607080;
  min-height: 100vh;
  padding: clamp(18px, 4vw, 54px);
  background:
    linear-gradient(135deg, rgba(47, 101, 149, 0.08), transparent 48%),
    #edf3f7;
  color: var(--ink);
}

.setup-panel {
  width: min(1160px, 100%);
  margin: 0 auto;
}

.setup-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: clamp(22px, 4vh, 40px);
}

.setup-eyebrow {
  margin: 0 0 7px;
  color: var(--blue);
  font-size: clamp(0.72rem, 1vw, 0.9rem);
  font-weight: 800;
  letter-spacing: 0.1em;
}

.setup-header h1 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.7rem);
  line-height: 1.02;
}

.setup-header p:last-child {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: clamp(1rem, 1.6vw, 1.25rem);
}

.service-status {
  flex: none;
  border: 1px solid #aed4bb;
  border-radius: 999px;
  padding: 8px 12px;
  background: #e1f3e7;
  color: #31704a;
  font-size: 0.72rem;
  font-weight: 800;
}

.setup-state,
.setup-notice,
.no-search-results {
  border-radius: 8px;
  padding: 18px;
  background: #ffffff;
  color: #526272;
  text-align: center;
}

.setup-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 1.1rem;
}

.loading-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--blue);
  animation: pulse 1s ease-in-out infinite alternate;
}

.setup-notice {
  margin-bottom: 18px;
  border: 1px solid #cfdbe5;
}

.setup-notice--warning {
  border-color: #e5cf7b;
  background: #fff9e8;
  color: #695719;
}

.setup-notice--error {
  border-color: #dfadb5;
  background: #fff1f3;
  color: #8e2435;
}

.search-field {
  display: block;
  max-width: 460px;
  margin-bottom: 24px;
}

.search-field span,
.manual-form label {
  display: block;
  margin-bottom: 6px;
  color: #526272;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.search-field input,
.manual-form input {
  width: 100%;
  border: 2px solid #9eb2c3;
  border-radius: 7px;
  padding: 13px 14px;
  background: #ffffff;
  color: var(--ink);
  font: inherit;
}

.search-field input:focus,
.manual-form input:focus {
  border-color: var(--blue);
  outline: 3px solid rgba(47, 101, 149, 0.18);
}

.unit-group + .unit-group {
  margin-top: 28px;
}

.unit-group h2 {
  margin: 0 0 10px;
  color: #526272;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
}

.panel-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.panel-card {
  min-width: 0;
  border: 1px solid #cbd7e0;
  border-radius: 10px;
  padding: 19px;
  background: #ffffff;
  color: var(--ink);
  text-align: left;
  box-shadow: 0 8px 24px rgba(31, 64, 89, 0.08);
  cursor: pointer;
  transition: transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
}

.panel-card:hover,
.panel-card:focus-visible {
  border-color: var(--blue);
  box-shadow: 0 12px 30px rgba(31, 64, 89, 0.16);
  outline: none;
  transform: translateY(-2px);
}

.panel-card-topline {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: var(--blue);
  font-size: 0.7rem;
  font-weight: 800;
}

.available-label {
  color: #31704a;
}

.panel-card strong {
  display: block;
  min-height: 2.5em;
  margin: 18px 0;
  font-size: clamp(1.15rem, 1.8vw, 1.55rem);
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.panel-action {
  display: block;
  border-radius: 5px;
  padding: 10px;
  background: var(--blue);
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 800;
  text-align: center;
}

.manual-section {
  margin-top: 26px;
  border-top: 1px solid #cfdbe5;
  padding-top: 18px;
}

.manual-toggle {
  border: 0;
  padding: 4px 0;
  background: transparent;
  color: var(--blue);
  font-weight: 800;
  text-decoration: underline;
  cursor: pointer;
}

.manual-form {
  max-width: 620px;
  margin-top: 16px;
}

.manual-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 9px;
}

.manual-controls button {
  border: 0;
  border-radius: 7px;
  padding: 0 20px;
  background: var(--blue);
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
}

.manual-error {
  margin: 7px 0 0;
  color: #a51f35;
  font-weight: 700;
}

.support-footer {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 30px;
  border-top: 1px solid #cfdbe5;
  padding-top: 17px;
  color: #657686;
  font-size: 0.78rem;
}

.support-footer strong {
  color: #40566a;
}

.current-url {
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes pulse {
  from { opacity: 0.35; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}

@media (max-width: 820px) {
  .setup-header,
  .support-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .panel-grid {
    grid-template-columns: 1fr;
  }

  .current-url {
    max-width: 100%;
  }
}

@media (max-width: 520px) {
  .setup-shell {
    padding: 18px 14px;
  }

  .manual-controls {
    grid-template-columns: 1fr;
  }

  .manual-controls button {
    min-height: 48px;
  }
}
</style>
